import { Stack } from '@chakra-ui/react'
import { Token } from '@chakra-ui/styled-system/dist/types/utils'
import * as CSS from 'csstype'
import React, { FC, memo, useCallback, useMemo } from 'react'
import { Attachment } from '../../generated'
import { UploadedFileRow } from './UploadedFileRow'

export interface UploadedFile {
  id: number
  name: string
  url: string
}

interface FileListProps {
  attachments: Array<Attachment> | Attachment | undefined
  canEdit?: boolean
  canMove?: boolean
  className?: string
  preview?: boolean
  onChange?: (attachments: Attachment[]) => void
  onTransfer?: (target: Attachment, direction: 'up' | 'down') => void
  fontSize?: Token<CSS.Property.FontSize | number, 'fontSizes'>
  canTransferByUp?: boolean
  canTransferByDown?: boolean
  canGrab?: boolean
}

export const _UploadedFiles: FC<FileListProps> = ({
  attachments,
  canEdit,
  canMove,
  onChange,
  className,
  preview,
  fontSize,
  onTransfer,
  canTransferByDown,
  canTransferByUp,
  canGrab,
}) => {
  const files = useMemo(() => {
    return attachments ? (Array.isArray(attachments) ? attachments : [attachments]) : []
  }, [attachments])

  const deleteFile = useCallback(
    (index?: number) => {
      if (index === undefined) return
      const newFiles = [...files]
      newFiles.splice(index, 1)
      onChange?.(newFiles)
    },
    [onChange, files]
  )

  const up = useCallback(
    (index) => {
      if (index === 0) {
        if (canTransferByUp) {
          onTransfer?.(files[0], 'up')
        }
        return
      }
      const newFiles = files.slice()
      const target = newFiles[index - 1]
      newFiles[index - 1] = newFiles[index]
      newFiles[index] = target
      onChange?.(newFiles)
    },
    [onChange, onTransfer, canTransferByUp, files]
  )

  const down = useCallback(
    (index) => {
      if (index === files.length - 1) {
        if (canTransferByDown) {
          const target = files[files.length - 1]
          onTransfer?.(target, 'down')
        }
        return
      }
      const newFiles = files.slice()
      const target = newFiles[index + 1]
      newFiles[index + 1] = newFiles[index]
      newFiles[index] = target
      onChange?.(newFiles)
    },
    [onChange, onTransfer, canTransferByDown, files]
  )


  return (
    <Stack spacing={1} className={className}>
      {files.map((u, index) => {
        const isUpDisabled = index === 0
        const isDownDisabled = index === files.length - 1

        return (
          <UploadedFileRow
            key={index}
            attachment={u as Attachment}
            canEdit={canEdit}
            canMove={canMove}
            handleDeleteFile={() => deleteFile(index)}
            preview={preview}
            isUpDisabled={!canTransferByUp && isUpDisabled}
            isDownDisabled={!canTransferByDown && isDownDisabled}
            upDownFuncs={canMove ? { index, up, down } : undefined}
            fontSize={fontSize}
            canGrab={canGrab}
          />
        )
      })}
    </Stack>
  )
}

export const UploadedFiles = memo(_UploadedFiles)
